/**
 * Assessment Eligibility Predicate
 *
 * PHASE 3 MIGRATION: Now delegates to Policy Engine for type eligibility.
 * Structural checks (options count) remain here as a thin wrapper.
 *
 * Previously: hardcoded MC-only check
 * Now: uses isAssessmentEligibleType() from governance pipeline
 *
 * ARCHITECTURE v4.0 — Phase 3, Step 3.1
 */

import { Question } from '../../../types'
import { isAssessmentEligibleType } from '../../../governance/pipeline/policyEngine'

/**
 * Check if a question is eligible for assessment use.
 * Delegates type eligibility to the policy engine (single source of truth).
 */
export function isAssessmentEligible(question: Question): boolean {
  const questionType = question.questionType || 'multiple_choice'

  // Policy engine determines which types are assessment-eligible
  if (!isAssessmentEligibleType(questionType)) return false

  // Structural checks for MC questions
  if (questionType === 'multiple_choice') {
    if (!question.options || question.options.length !== 4) return false
  }

  // Legacy interactiveConfig blocks assessment (strict mode)
  if (
    question.interactiveConfig &&
    Object.keys(question.interactiveConfig).length > 0
  )
    return false

  return true
}

export function getIneligibilityReason(question: Question): string | null {
  const questionType = question.questionType || 'multiple_choice'

  if (!isAssessmentEligibleType(questionType))
    return `Question type '${questionType}' is not assessment-eligible per policy`

  if (questionType === 'multiple_choice') {
    if (!question.options || question.options.length !== 4)
      return `Has ${question.options?.length || 0} options, needs exactly 4`
  }

  if (
    question.interactiveConfig &&
    Object.keys(question.interactiveConfig).length > 0
  )
    return 'Has interactive configuration (legacy — migrate to data.{type})'

  return null
}

export function filterAssessmentEligible(questions: Question[]): Question[] {
  return questions.filter(isAssessmentEligible)
}

export function getEligibilityStats(questions: Question[]): {
  total: number
  eligible: number
  ineligible: number
  percentage: number
  reasons: Record<string, number>
} {
  const total = questions.length
  const eligible = questions.filter(isAssessmentEligible).length
  const ineligible = total - eligible
  const reasons: Record<string, number> = {}
  questions.forEach((q) => {
    if (!isAssessmentEligible(q)) {
      const reason = getIneligibilityReason(q) || 'Unknown'
      reasons[reason] = (reasons[reason] || 0) + 1
    }
  })
  return {
    total,
    eligible,
    ineligible,
    percentage: total > 0 ? Math.round((eligible / total) * 100) : 0,
    reasons,
  }
}
